import { JMCHtml } from "@components/JMCHtml/JMCHtml";
import { JMCLink, regexAnonymizeEmail } from "@components/JMCLink/JMCLink";
import { ButtonImpression } from "@jmc/core/src/components/ButtonImpression/index";
import { FileDownloadImpression } from "@jmc/core/src/components/FileDownloadImpression/index";
import { useLocale } from "@jmc/core/src/hooks/useLocale/index";
import { Button, ButtonSizes } from "@jmc/solid-design-system/src/components/atoms/Button/Button";
import { Icon, jnjLock } from "@jmc/solid-design-system/src/components/atoms/Icon/Icon";
import urlHelper from "@jmc/solid-design-system/src/utils/url-helper";
import { useJnjBranding } from "@jmc/utils/hooks/useJnjBranding";
import { default as fileHelper } from "@jmc/utils/utils/file-helper";
import { mdiLock } from "@mdi/js";
import { ValidAuthResults } from "@redux/modules/authValid";
import { CMSButtonColor, CMSButtonProps, CMSFootnotes } from "@types";
import { isAuthorized } from "@utils/authorizer";
import { default as cmsMapper } from "@utils/cms-mapper";
import axios from "axios";
import isEqual from "lodash/isEqual";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";

type Props = {
    button: CMSButtonProps;
    placement?: string;
    footnotes?: CMSFootnotes[];
    size?: ButtonSizes;
    accessoryRight?: JSX.Element;
    accessoryLeft?: JSX.Element;
    disabled?: boolean;
    fullWidth?: boolean;
    isAllowedOnMedical?: boolean;
};

export const getButtonProps = ({
    buttonText,
    variant = "Naked",
    color = "Primary Color",
    internalLink = "",
    externalLink = "",
}: {
    buttonText: string;
    variant?: "Outlined" | "Contained" | "Naked";
    color?: CMSButtonColor;
    internalLink?: string;
    externalLink?: string;
}): CMSButtonProps => {
    return {
        text: buttonText,
        variant: variant,
        color: color,
        noSpacing: true,
        flexibleWidth: false,
        link_group: {
            internal_link: [
                {
                    page_url: internalLink,
                },
            ],
            link_continued: "",
            external_link: externalLink,
            file_asset: {
                url: "",
            },
            anchor_id: "",
        },
    };
};

export const JMCButton = (props: Props): JSX.Element => {
    const {
        button,
        placement,
        footnotes,
        size = "medium",
        accessoryRight,
        accessoryLeft,
        disabled = false,
        fullWidth = false,
        isAllowedOnMedical = false,
        ...other
    } = props;
    const text = button?.text;
    const locale = useLocale() || "en-us";
    const buttonProps = cmsMapper.mapButtonProps(button);
    const { color } = buttonProps.buttonProps;
    const { url, external } = buttonProps.link;
    const mailToUrl = url?.includes("mailto:");
    const phoneNumberUrl = url?.startsWith("tel:");
    const eventurl = url?.match(/\/services\/events\/([a-zA-Z0-9]+)$/);
    const [medicalEvent, setMedicalEvent] = useState(false);
    const { jnjFullBranded } = useJnjBranding();

    useEffect(() => {
        const controller = new AbortController();
        let unmounted = false;
        const fetchMedical = async (): Promise<void> => {
            try {
                const response = await axios.get(
                    `${process.env.GATSBY_API_ORIGIN_URL}/${process.env.GATSBY_API_VERSION}/events/medical/${eventurl[1]}`,
                    {
                        signal: controller.signal,
                    },
                );
                if (response?.data?.medical && !unmounted) {
                    setMedicalEvent(true);
                }
            } catch (error) {
                if (error.name === "CanceledError") return;
            }
        };
        if (eventurl) {
            fetchMedical();
        }
        return () => {
            if (eventurl) {
                unmounted = true; //prevents fetchmedical from attempting to update the state if the component is unmounted
                controller.abort(); //abort axios request if component unmounts before request is finished
            }
        };
    }, [eventurl]);

    if (!button) return null;

    const determineUrl = (): string => {
        if (mailToUrl) {
            let subString = url.split(":").pop().split("@")[0];
            subString = url.replace(new RegExp(subString, "g"), "email");
            if (subString.includes("?")) subString = subString.slice(0, subString.indexOf("?"));
            return subString;
        }
        if (phoneNumberUrl) {
            return `callto phone-number`;
        }
        return external ? url : urlHelper.getFullUrl(url, locale, external);
    };

    const authData = useSelector(
        ({ authValid }: { authValid: { data: ValidAuthResults } }) => authValid?.data,
        isEqual,
    );

    const showLock =
        buttonProps?.link?.access_level &&
        !isAuthorized(authData, buttonProps?.link?.access_level, buttonProps?.link?.url);

    const getButtonIcon = (): string => {
        if (showLock) {
            return mdiLock;
        }
        return null;
    };

    const getJnjLock = (): JSX.Element => {
        if (showLock && jnjFullBranded) {
            return <Icon color="inherit" data-test-id="button.lock" icon={jnjLock} type="jnj" />;
        }
        return null;
    };

    const targetUrl = url && determineUrl();
    const Component = (): JSX.Element => {
        return (
            <JMCLink
                params={buttonProps.link?.params}
                url={buttonProps.link.url}
                commercial={eventurl ? !medicalEvent : buttonProps.link.commercial}
                external={eventurl ? false : buttonProps.link.external}
                fullWidth={fullWidth}
                anchor_id={buttonProps?.link?.anchor_id}
                isFileAsset={buttonProps?.link?.isFileAsset}
                buttonLink={jnjFullBranded || false}
                isAllowedOnMedical={isAllowedOnMedical}
                noImpression
            >
                <ButtonImpression
                    name={
                        mailToUrl
                            ? text?.replace(regexAnonymizeEmail, "email@")
                            : phoneNumberUrl
                              ? `phone-number`
                              : text
                    }
                    category={phoneNumberUrl ? "primary contained" : color}
                    placement={placement}
                    targeturl={targetUrl}
                    targettype={mailToUrl ? "mailto" : phoneNumberUrl ? "callto" : external ? "external" : "internal"}
                    fullWidth={fullWidth}
                >
                    <Button
                        startIcon={!jnjFullBranded && getButtonIcon()}
                        disabled={disabled}
                        {...buttonProps.buttonProps}
                        data-test-id={`button.${text}`}
                        size={jnjFullBranded ? size : "medium"}
                        accessoryLeft={accessoryLeft}
                        accessoryRight={
                            (getJnjLock() || accessoryRight) && (
                                <>
                                    {getJnjLock()}
                                    {accessoryRight}
                                </>
                            )
                        }
                        fullWidth={fullWidth}
                        {...other}
                    >
                        <JMCHtml footnotes={footnotes}>{text}</JMCHtml>
                    </Button>
                </ButtonImpression>
            </JMCLink>
        );
    };

    if (buttonProps.fileUrl) {
        return (
            <FileDownloadImpression name={fileHelper.getFileName(buttonProps.fileUrl)}>
                <Component />
            </FileDownloadImpression>
        );
    }

    return <Component />;
};
